import { COMMA, ENTER } from '@angular/cdk/keycodes'
import { AfterContentInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { AbstractControl, FormBuilder, UntypedFormControl, Validators } from '@angular/forms'
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog'
import { MatSelectChange } from '@angular/material/select'
import { Router } from '@angular/router'
import {
  ApiService,
  AuthService,
  isValidCPT,
  Procedure,
  procedureCodeValidator,
  Product,
  ProductCategory,
  ProductModel,
  ProductTypes,
  ProgressIndicatorService,
  SelectOption,
  ServiceType,
  ServiceTypes,
} from '@mmx/shared'
import Fuse from 'fuse.js'
import { eq, isArray } from 'lodash'
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs'
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators'

@Component({
  selector: 'mmx-product-dialog-component',
  templateUrl: './product-dialog.component.html',
  styleUrls: ['./product-dialog.component.scss'],
  standalone: false,
})
export class ProductDialogComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild('codeFilterInput') codeFilterInput: ElementRef<HTMLInputElement>
  isLoading$ = new BehaviorSubject(false)
  product: ProductModel
  fromProceduresCard = false
  isExistingProduct = false

  productData = this.fb.group({
    type: this.fb.control<Product.TYPE>(null, Validators.required),
    status: this.fb.control<Product.STATUS>(null),
    sku: this.fb.control<string | null>(null),
    eid: this.fb.control<string | null>(null),
    /** @deprecated */
    code: this.fb.control<string>(null),
    codes: this.fb.control<string[]>([], [Validators.required, procedureCodeValidator]),
    modifiers: this.fb.control<Record<string, string[]>>({}),
    serviceCodes: this.fb.control<string[]>([]),
    usingRecommendServiceCodes: this.fb.control<boolean>(true),
    updateOtherProducts: this.fb.control<boolean>(true),
    updateSystemServiceCodes: this.fb.control<boolean>(false),
    name: this.fb.control<string>(null, Validators.required),
    resources: this.fb.control<string[]>([]),
    description: this.fb.control<string | null>(null),
    categories: this.fb.control<string[]>([]),
    primaryCategory: this.fb.control<string | null>(null),
    defaultDuration: this.fb.control<number | null>(null, Validators.pattern(/^[0-9]+$/)),
  })

  productCategories$: Observable<ProductCategory[]> = this.restService.getProductCategories()
    .pipe(
      shareReplay(1),
    )

  selectedProductCategories$: Observable<ProductCategory[]> = combineLatest([
    this.productData.get('categories').valueChanges,
    this.productCategories$,
  ])
    .pipe(
      filter(([selectedCategories, categories]) => !!selectedCategories && !!categories),
      map(([selectedCategories, categories]) => {
        return categories.filter((category) => {
          return selectedCategories.includes(category.id)
        })
      }),
    )

  // procedure: Procedure
  procedures: Map<string, Procedure> = new Map()
  productTypes: SelectOption<string>[] = ProductTypes
  serviceTypes = ServiceTypes

  updatedProduct$ = this.productData.valueChanges.pipe(
    startWith(this.productData.value),
    map(() => {
      const value = this.productData.value

      return new ProductModel({
        ...this.product.toJSON(),
        type: value.type,
        sku: value.sku,
        eid: value.eid,
        codes: value.codes,
        serviceCodes: value.serviceCodes,
        usingRecommendServiceCodes: value.usingRecommendServiceCodes,
        name: value.name,
        resources: value.resources,
        description: value.description,
        categories: value.categories,
        categoryId: value.primaryCategory,
        defaultDuration: value.defaultDuration,
      })
    }),
  )

  filteredCptCode$: Observable<Procedure[]> = combineLatest([
    this.productData.get('code').valueChanges,
  ]).pipe(
    startWith(['', false]),
    distinctUntilChanged((a, b) => eq(a, b)),
    concatMap((params) => {
      const value = (params[0] ?? '') as string

      if (isValidCPT(value) && !this.procedures.has(value)) {
        return this.loadProcedureData(value).pipe(
          catchError(() => {
            if (value) {
              return of(this.filterAutocomplete(value))
            } else {
              return of([...this.procedures.values()])
            }
          }),
        )
      } else if (value) {
        return of(this.filterAutocomplete(value))
      } else {
        return of([...this.procedures.values()])
      }
    }),
    map((value) => (isArray(value) ? value : [value])),
    map((items) => {
      const selectedCodes = this.productData.get('codes').value || []
      return items.filter((procedure) => !selectedCodes.includes(procedure.code))
    }),
  )

  serviceFilterCtrl = new UntypedFormControl()
  filteredServiceTypes: ReplaySubject<ServiceType[]> = new ReplaySubject<
    ServiceType[]
  >(1)
  showAdvancedSettings = false
  errors: any
  separatorKeysCodes: number[] = [ENTER, COMMA]
  isPatientPalEmployee = false

  get isMedicalType() {
    return [
      Product.TYPE.MEDICAL_PROCEDURE,
      Product.TYPE.DENTAL_PROCEDURE,
    ].includes(this.productData.value.type)
  }

  private fuseProcedures: Fuse<Procedure> = new Fuse([], {
    keys: ['code', 'name'],
    useExtendedSearch: true,
    shouldSort: true,
  })

  private fuseServiceTypes = new Fuse(this.serviceTypes, {
    keys: ['code', 'name'],
    useExtendedSearch: true,
    shouldSort: true,
  })

  private _onDestroy = new Subject<void>()

  constructor(
    public dialogRef: MatDialogRef<ProductDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private progressService: ProgressIndicatorService,
    private restService: ApiService,
    private fb: FormBuilder,
    private auth: AuthService,
  ) {
    this.product = (this.data ? this.data.product : null) || new ProductModel()
    this.fromProceduresCard = this.data?.fromProceduresCard
    this.filteredServiceTypes.next(this.serviceTypes.slice())
    this.isPatientPalEmployee = this.auth.user.isPatientPalEmployee
    this.isExistingProduct = this.data?.product != null
  }

  ngOnInit() {
    this.productData.patchValue({
      type: this.product.type || Product.TYPE.MEDICAL_PROCEDURE,
      status: this.product.status ?? Product.STATUS.ACTIVE,
      sku: this.product.sku,
      eid: this.product.eid,
      code: this.product.code,
      codes: this.product.getCPTCodes(),
      serviceCodes: this.product.serviceCodes,
      usingRecommendServiceCodes: this.product.usingRecommendServiceCodes,
      name: this.product.name,
      resources: this.product.resources,
      description: this.product.description,
      categories: this.product.categories,
      primaryCategory: this.product.categoryId,
      defaultDuration: this.product.defaultDuration,
    })

    const isLegacy = Array.isArray(this.product.modifiers) && this.product.code
    const modifiers: Record<string, string[]> = isLegacy
      ? { [this.product.code]: this.product.modifiers as string[] }
      : this.product.modifiers as Record<string, string[]>
    this.productData.get('modifiers').patchValue(modifiers)

    // if (this.productData.value.type === Product.TYPE.MEDICAL_PROCEDURE) {
    //   // ensure we show a validation error when the product dialog is opened
    //   this.productData.get('code').markAsTouched()
    // }

    // trigger type change to set validators
    this.onTypeChanged()

    this.productData.controls.name.valueChanges
      .pipe(
        takeUntil(this._onDestroy),
        filter((value) => value.length > 0),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.productData.get('code').disable()
          this.isLoading$.next(true)
        }),
        switchMap((value) => this.restService.getProcedureCodeSuggestions(value).pipe(
          catchError(() => {
            // The suggestions service may be unavailable (e.g. /cms/suggest 500).
            // Degrade gracefully so the CPT field stays manually editable and
            // keep the stream alive for subsequent edits.
            this.productData.get('code').enable()
            this.isLoading$.next(false)
            return of([{ query: value, suggestions: [] }])
          }),
        )),
        map((codeSuggestions) => {
          for (const procedureData of codeSuggestions[0].suggestions) {
            const procedure = new Procedure(procedureData)
            this.procedures.set(procedure.code, procedure)
          }
          this.fuseProcedures.setCollection([...this.procedures.values()])
          this.productData.get('code').enable()
          this.isLoading$.next(false)

          // trigger a change to the code so it has suggestions and can show autocomplete on focus
          this.productData.get('code').updateValueAndValidity({
            onlySelf: true,
            emitEvent: true,
          })
        }),
      )
      .subscribe()

    if (this.product.name != null && this.product.name.length > 0) {
      // trigger change to cause it to load CPT code suggestions
      this.productData.controls.name.updateValueAndValidity({
        onlySelf: true,
        emitEvent: true,
      })
    }

    this.serviceFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((search) => {
        this.filterService(search)
      })
  }

  ngAfterContentInit(): void {
    this.productData.get('code').disable()
    this.productData.updateValueAndValidity()
  }

  ngOnDestroy(): void {
    this._onDestroy.next()
    this._onDestroy.complete()
  }

  onTypeChanged(event?: MatSelectChange) {
    const type = event?.value || this.productData.get('type').value
    const skuControl = this.productData.get('sku')
    const codeControl = this.productData.get('codes')

    if (type === Product.TYPE.MEDICAL_PROCEDURE) {
      skuControl.setValue('')
      skuControl.setValidators([])
      codeControl.setValidators([Validators.required, procedureCodeValidator])
    } else {
      skuControl.setValidators(Validators.required)
      codeControl.setValidators([])
    }

    skuControl.updateValueAndValidity()
    codeControl.updateValueAndValidity()
  }

  onCategoryChanged({ value }: MatSelectChange) {
    if (
      value?.length > 0 &&
      (!this.productData.value.primaryCategory || !value.includes(this.productData.value.primaryCategory))
    ) {
      this.productData.controls.primaryCategory.setValue(value[0])
      this.productData.controls.primaryCategory.updateValueAndValidity()
    }
  }

  getSystemServiceTypeDescriptions() {
    let serviceCodes = this.product?.recommendedServiceCodes
    const codes = this.productData.value.codes

    if (codes?.length) {
      const procedure = this.procedures.get(codes[0])
      if (procedure?.serviceCodes?.length > 0) {
        serviceCodes = procedure.serviceCodes
      }
    }

    if (serviceCodes?.length > 0) {
      const serviceTypes = serviceCodes.map((code) => this.serviceTypes.find((type) => type.code === code))
      return serviceTypes.filter(Boolean)
    }
    return []
  }

  getErrorMessage(controlName: string) {
    if (this.productData.controls[controlName].hasError('pattern')) {
      return 'Duration must be number.'
    } else if (this.productData.get(controlName).hasError('procedureCode')) {
      return 'Invalid CPT/HCPCS code'
    } else {
      return 'This field is required'
    }
  }

  optionSelected(value: string) {
    if (!isValidCPT(value)) {
      return
    }

    const procedure = this.procedures.get(value)

    const codesControl: AbstractControl<string[]> = this.productData.get('codes')
    codesControl.setValue([...new Set([...codesControl.value, value])])
    this.codeFilterInput.nativeElement.value = ''
    this.productData.get('code').setValue('')

    if (procedure) {
      const values = this.productData.value

      if (!isArray(values.serviceCodes) || values.serviceCodes.length === 0) {
        this.productData.get('serviceCodes').setValue(procedure.serviceCodes)
        this.productData.get('serviceCodes').updateValueAndValidity()
      }
    }
  }

  removeCode(code: string) {
    const codesControl: AbstractControl<string[]> = this.productData.get('codes')
    codesControl.setValue(codesControl.value.filter((c) => c !== code))
  }

  toggleAdvancedSettings() {
    this.showAdvancedSettings = !this.showAdvancedSettings
  }

  onSubmit() {
    if (this.productData.invalid) {
      return
    }

    const values = this.productData.value
    this.product.type = values.type
    if (this.productData.controls.status.dirty) {
      // only when somebody actually chose one. Some callers open this dialog with a projection
      // that leaves status out, and writing the control's default would silently reactivate it
      this.product.status = values.status
    }
    this.product.sku = this.product.type === Product.TYPE.MEDICAL_PROCEDURE ? '' : values.sku
    this.product.code = null
    this.product.codes = values.codes
    this.product.modifiers = values.modifiers
    this.product.serviceCodes = values.serviceCodes
    this.product.usingRecommendServiceCodes = values.usingRecommendServiceCodes
    this.product.eid = values.eid
    this.product.name = values.name
    this.product.description = values.description
    this.product.categoryId = values.primaryCategory
    this.product.categories = values.categories
    this.product.defaultDuration = values.defaultDuration
    this.product.resources = values.resources

    const meta = {
      updateOtherProducts: values.updateOtherProducts,
      updateSystemServiceCodes: values.updateSystemServiceCodes,
    }

    this.restService.upsertProduct(this.product, meta)
      .pipe(
        tap(() => this.isLoading$.next(true)),
        takeUntil(this._onDestroy),
      )
      .subscribe({
        next: () => {
          this.isLoading$.next(false)
          this.progressService.snack(
            `Product ${!!this.product.id ? 'updated' : 'created'} successfully`,
          )
          this.dialogRef.close(this.product)
        },
        error: (err) => {
          this.isLoading$.next(false)
          if (err.error) {
            this.errors = err.error.errors
            return
          }

          this.progressService.snack(
            `An error occurred while ${!!this.product.id ? 'updating' : 'creating'} product. Please try again`,
          )
        },
      })
  }

  goToFeeSchedule(id: string) {
    this.dialogRef.close(this.product)

    this.router.navigate(['settings', 'fee-schedules', id], {
      queryParams: {
        code: this.product.code ?? '',
      },
    })
  }

  private loadProcedureData(code: string) {
    return this.restService.getProcedureInfo(code, true).pipe(
      takeUntil(this._onDestroy),
      tap((procedure) => {
        this.procedures.set(code, procedure)
      }),
    )
  }

  private filterAutocomplete(val: string): Procedure[] {
    return this.fuseProcedures.search(`'${val}`).map((result) => result.item)
  }

  private filterService(search: string = this.serviceFilterCtrl.value) {
    if (!this.serviceTypes) {
      return
    }
    if (!search) {
      this.filteredServiceTypes.next(this.serviceTypes.slice())
    } else {
      this.filteredServiceTypes.next(
        this.fuseServiceTypes.search(`'${search}`).map((result) => result.item),
      )
    }
  }
}
